// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FulfillmentFilterMenuItem } from '@/components/fulfillment-filter-menu-item';
import {
  FULFILLMENT_STATUSES,
  FULFILLMENT_STATUS_LABELS,
  FulfillmentStatus,
} from '@/constants/fulfillment-status';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Menu } from '@mui/material';
import { useRef } from 'react';

interface Props {
  /**
   * The current project fulfillment state filter values.
   */
  value: FulfillmentStatus[];

  /**
   * A callback fired when the filter values change.
   */
  onChange: (value: FulfillmentStatus[]) => void;

  /**
   * The label for the filter button.
   */
  label?: string;
}

/**
 * A controlled component that allows the user to filter by project fulfillment status.
 * Multiple options can be selected at the same time.
 */
export function ProjectFulfillmentFilterControl(props: Props) {
  const menu = useDisclosure();
  const anchorElementRef = useRef<HTMLButtonElement>(null);

  const options = FULFILLMENT_STATUSES.map((status) => ({
    id: status,
    label: FULFILLMENT_STATUS_LABELS[status],
    isActive: props.value.includes(status),
  }));

  const handleChangeOption = (id: FulfillmentStatus, isActive: boolean) => {
    if (isActive) {
      props.onChange([...props.value, id]);
    } else {
      props.onChange(props.value.filter((v) => v !== id));
    }
  };

  const count = options.filter((option) => option.isActive).length;

  return (
    <>
      <FilterMenuButton
        ref={anchorElementRef}
        count={count}
        isDisabled={false}
        onClick={menu.open}
      >
        {props.label ?? 'Project fulfillment'}
      </FilterMenuButton>
      <Menu
        open={menu.isOpen}
        anchorEl={anchorElementRef.current}
        onClose={menu.close}
      >
        {options.map((option) => (
          <FulfillmentFilterMenuItem
            key={option.id}
            status={option.id}
            label={option.label}
            isActive={option.isActive}
            onChange={(isActive) => handleChangeOption(option.id, isActive)}
          />
        ))}
      </Menu>
    </>
  );
}
